/**
 * remembered-folder.js — window.__rememberedFolder
 *
 * Папка на диске человека, которую тул помнит между визитами, — и ответ на
 * вопрос «хватит ли в ней места», заданный ДИСКУ этой папки, а не хранилищу
 * браузера.
 *
 * Зачем (24.10.2026, отзыв #2012, /screen-recorder, Премиум): запись
 * трансляции трижды останавливалась на 30, 8 и 2 минуте. Яндекс Браузер давал
 * странице 270–330 МБ хранилища (системный диск почти полон: грант ≈
 * свободно − 1 ГиБ), прошлый дубль лежал в истории, а скачанная копия того же
 * дубля — ещё раз в «Загрузках». Файл, записанный прямо в выбранную папку,
 * хранилищу браузера не принадлежит: его грант не спрашивается, браузерная
 * чистка его не стирает, копии нет — предел только диск. Замер устройства
 * Chromium (swap `.crswap` рядом с целью, close() ≈ 17 с на ГБ) —
 * docs/why/primitives/storage-room.md § «Диск отказал».
 *
 * Дескриптор папки клонируется в IndexedDB; право писать браузер отдаёт заново
 * только из ЖЕСТА (requestPermission вне клика отвечает отказом), поэтому
 * pick() и grant() зовутся первым await в нажатии.
 *
 *   __rememberedFolder.available()            → bool (Chromium на компьютере)
 *   __rememberedFolder.load(key)               → Promise<{handle,name}|null>
 *   __rememberedFolder.state(entry)            → Promise<'granted'|'prompt'|'denied'>
 *   __rememberedFolder.pick(key, {startIn,id}) → Promise<{handle,name}|null>  (жест)
 *   __rememberedFolder.grant(entry)            → Promise<'granted'|'prompt'|'denied'> (жест)
 *   __rememberedFolder.forget(key)             → Promise<void>
 *   __rememberedFolder.uniqueFile(dir, base, ext) → Promise<FileSystemFileHandle>
 *       новый файл «base.ext», «base (2).ext»… — чужой файл не перезаписывается
 *   __rememberedFolder.room(dir, bytes)        → Promise<true|false|null>
 *       влезет ли ещё `bytes` на диск ЭТОЙ папки: пробный файл резервирует их
 *       truncate() и уходит. false — диск отказал (квота/место), null — ответа
 *       нет (папку не открыть). Проба живёт на своём файле: отказанный
 *       truncate убивает поток, и мерить им файл, в который идёт запись,
 *       нельзя.
 *
 * Сторож — tests/js/remembered-folder.test.mjs.
 */
(function (w) {
    'use strict';
    if (w.__rememberedFolder) return;

    var DB = 'tmb-folders', STORE = 'handles';
    var PROBE = 'timbrica-room-check.tmp';

    function ev(f, more) {
        try { w.__ae && w.__ae('feature_use', Object.assign({ slug: w.__TOOL_SLUG || '', f: f }, more || {})); } catch (_) {}
    }
    function available() {
        try { return !!(w.isSecureContext && typeof w.showDirectoryPicker === 'function' && w.indexedDB); } catch (_) { return false; }
    }

    function idb() {
        return new Promise(function (res, rej) {
            try {
                var r = w.indexedDB.open(DB, 1);
                r.onupgradeneeded = function () { r.result.createObjectStore(STORE); };
                r.onsuccess = function () { res(r.result); };
                r.onerror = function () { rej(r.error); };
            } catch (e) { rej(e); }
        });
    }
    function idbDo(mode, fn) {
        return idb().then(function (db) {
            return new Promise(function (res, rej) {
                var tx = db.transaction(STORE, mode), st = tx.objectStore(STORE), q = fn(st);
                q.onsuccess = function () { res(q.result); };
                q.onerror = function () { rej(q.error); };
            });
        });
    }

    function entryOf(handle) { return handle ? { handle: handle, name: handle.name || '' } : null; }

    function load(key) {
        if (!available()) return Promise.resolve(null);
        return idbDo('readonly', function (st) { return st.get(String(key)); })
            .then(function (h) { return h && h.kind === 'directory' ? entryOf(h) : null; })
            .catch(function () { return null; });
    }

    function state(entry) {
        var h = entry && entry.handle;
        if (!h) return Promise.resolve('denied');
        if (typeof h.queryPermission !== 'function') return Promise.resolve('granted');
        return h.queryPermission({ mode: 'readwrite' }).catch(function () { return 'prompt'; });
    }

    function pick(key, opts) {
        opts = opts || {};
        if (!available()) return Promise.resolve(null);
        var po = { mode: 'readwrite', id: String(opts.id || key).slice(0, 32).replace(/[^a-zA-Z0-9_-]/g, '_') };
        if (opts.startIn) po.startIn = opts.startIn;
        var t0 = Date.now(), p;
        try { p = w.showDirectoryPicker(po); } catch (e) { ev('folder_pick', { why: 'blocked' }); return Promise.resolve(null); }
        return p.then(function (handle) {
            ev('folder_pick', { why: 'picked', ms: Date.now() - t0 });
            return idbDo('readwrite', function (st) { return st.put(handle, String(key)); })
                .catch(function () {}).then(function () { return entryOf(handle); });
        }, function (e) {
            var ms = Date.now() - t0;
            // Встроенный браузер без диалога отвечает AbortError мгновенно.
            ev('folder_pick', { why: e && e.name === 'AbortError' ? (ms < 400 ? 'no_dialog' : 'cancel') : 'blocked', ms: ms });
            return null;
        });
    }

    function grant(entry) {
        var h = entry && entry.handle;
        if (!h) return Promise.resolve('denied');
        if (typeof h.requestPermission !== 'function') return Promise.resolve('granted');
        return h.requestPermission({ mode: 'readwrite' }).then(function (s) {
            ev('folder_grant', { state: s });
            return s;
        }, function () { return 'denied'; });
    }

    function forget(key) {
        return idbDo('readwrite', function (st) { return st.delete(String(key)); }).catch(function () {});
    }

    function exists(dir, name) {
        return dir.getFileHandle(name).then(function () { return true; }, function (e) {
            return !(e && e.name === 'NotFoundError') ? true : false;
        });
    }
    // Имя, которого в папке ещё нет: чужой файл не перезаписывается никогда.
    function uniqueFile(dir, base, ext) {
        base = String(base || 'file').replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 120) || 'file';
        ext = String(ext || '').replace(/^\./, '');
        var tail = ext ? '.' + ext : '';
        function attempt(n) {
            var name = n < 2 ? base + tail : base + ' (' + n + ')' + tail;
            return exists(dir, name).then(function (taken) {
                if (taken && n < 500) return attempt(n < 2 ? 2 : n + 1);
                return dir.getFileHandle(name, { create: true });
            });
        }
        return attempt(1);
    }

    function isRoomRefusal(e) {
        var n = (e && e.name) || '';
        return n === 'QuotaExceededError' || n === 'NoModificationAllowedError' || (e && e.code === 22) ||
            /quota|space|full|disk/i.test(String((e && e.message) || ''));
    }
    // Влезет ли `bytes` на диск папки: пробный файл резервирует их и уходит.
    function room(dir, bytes) {
        if (!dir || typeof dir.getFileHandle !== 'function') return Promise.resolve(null);
        var n = Math.max(1, Math.round(+bytes || 0)), fh = null, wr = null;
        function cleanup() {
            var a = wr ? Promise.resolve().then(function () { return wr.abort(); }).catch(function () {}) : Promise.resolve();
            return a.then(function () { return dir.removeEntry(PROBE).catch(function () {}); });
        }
        return dir.getFileHandle(PROBE, { create: true }).then(function (h) {
            fh = h;
            return fh.createWritable();
        }).then(function (s) {
            wr = s;
            return wr.truncate(n);
        }).then(function () {
            return cleanup().then(function () { return true; });
        }, function (e) {
            return cleanup().then(function () {
                if (isRoomRefusal(e)) { ev('folder_room_refused', { mb: Math.round(n / 1048576), name: (e && e.name) || '' }); return false; }
                return null;
            });
        });
    }

    w.__rememberedFolder = {
        available: available, load: load, state: state, pick: pick, grant: grant, forget: forget,
        uniqueFile: uniqueFile, room: room, PROBE_NAME: PROBE
    };
})(typeof window !== 'undefined' ? window : globalThis);
